import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AddDelayModal } from './AddDelayModal';
import { AddProjectModal } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const fmtDT = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
};

const DOC_TYPE_LABEL = {
  Piece_identite: "Piece d'identite",
  Bulletins_salaire: "Bulletins de salaire",
  Avis_imposition: "Avis d'imposition",
  Releves_bancaires: "Releves bancaires",
  Justificatifs_epargne: "Justificatifs d'epargne",
  Autre: "Autre",
};

/**
 * Vue unifiee "Dossier client" - aggregée sur la fiche lead.
 * 5 panneaux : Mandat | Pieces deposees | Courtier | Projets | Delais & Notes
 */
export default function ClientWorkspaceSection({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [sendingCourtier, setSendingCourtier] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddDelay, setShowAddDelay] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    // Charger les projets pour le AddDelayModal
    axios.get(`${API}/projects`).then(r => setAllProjects(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crm/leads/${leadId}/workspace`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement dossier client');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // Refresh toutes les 30s pour voir les nouveaux uploads en temps reel
  useEffect(() => {
    const itv = setInterval(load, 30000);
    return () => clearInterval(itv);
  }, [load]);

  const generateLink = async () => {
    if (!window.confirm("Generer un nouveau lien d'upload (30 jours) et l'envoyer au client par email ?")) return;
    setSendingLink(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${leadId}/upload-link`, { valid_days: 30, send_email: true });
      if (r.data.email_sent) {
        toast.success('Lien envoye au client par email.');
      } else {
        toast.info('Lien genere (email non configure).');
      }
      try {
        await navigator.clipboard.writeText(r.data.upload_link);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch { /* ignore */ }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur generation lien');
    } finally {
      setSendingLink(false);
    }
  };

  const exportZip = async () => {
    setExportingZip(true);
    try {
      const r = await axios.get(`${API}/crm/leads/${leadId}/documents-zip`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="(.+)"/);
      a.download = m ? m[1] : 'Pieces_client.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Dossier complet exporte (zip).');
    } catch (e) {
      let detail = 'Erreur export zip';
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* noop */ }
      }
      toast.error(detail);
    } finally {
      setExportingZip(false);
    }
  };

  const sendToCourtier = async () => {
    const courtierEmail = data?.courtier?.email;
    if (!window.confirm(`Envoyer le zip des pieces par email au courtier (${courtierEmail}) ?`)) return;
    setSendingCourtier(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${leadId}/documents-send-courtier`);
      toast.success(`Dossier envoye a ${r.data.to} (${(r.data.size_bytes / 1024).toFixed(0)} Ko).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi au courtier');
    } finally {
      setSendingCourtier(false);
    }
  };

  const sendReminder = async () => {
    if (!window.confirm('Envoyer une relance email au client avec la liste des pieces obligatoires manquantes ?')) return;
    setSendingReminder(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${leadId}/documents-reminder`);
      toast.success(`Relance envoyee a ${r.data.to} (${r.data.missing.length} piece(s) manquante(s)).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi relance');
    } finally {
      setSendingReminder(false);
    }
  };

  const copyLink = async () => {
    if (!data?.upload_link?.token) return;
    const url = `${window.location.origin}/upload-documents/${data.upload_link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Lien copie dans le presse-papier.');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const revokeLink = async () => {
    if (!window.confirm("Revoquer le lien d'upload actuel ? Le client ne pourra plus deposer ses pieces avec l'ancien lien.")) return;
    try {
      await axios.delete(`${API}/crm/leads/${leadId}/upload-link`);
      toast.success('Lien revoque.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur revocation');
    }
  };

  const saveQuickNote = async () => {
    const text = (noteText || '').trim();
    if (!text) return;
    setSavingNote(true);
    try {
      // Recupere le quick_notes actuel et y prepend la nouvelle note datee
      const cur = data?.recent_notes?.find(n => n.source === 'lead')?.content || '';
      const now = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
      const newContent = `[${now}] ${text}\n\n${cur}`.trim();
      await axios.put(`${API}/crm/leads/${leadId}`, { quick_notes: newContent });
      toast.success('Note ajoutee.');
      setNoteText('');
      setShowAddNote(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde note');
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="realease-card p-6" data-testid="workspace-loading">
        <div className="animate-pulse h-6 bg-gray-200 rounded w-1/3 mb-3" />
        <div className="animate-pulse h-3 bg-gray-100 rounded w-2/3" />
      </div>
    );
  }
  if (!data) return null;

  const signedContract = data.signed_contract;
  const draftContracts = (data.contracts || []).filter(c => c.status !== 'signed');
  const uploads = data.uploads || [];
  const link = data.upload_link;
  const projects = data.projects || [];
  const delays = data.delays || [];
  const courtier = data.courtier;
  const drive = data.drive_folder;
  const docStatus = data.documents_status;

  // Grouper les uploads par type de document
  const uploadsByType = uploads.reduce((acc, u) => {
    const key = u.document_type || 'Autre';
    if (!acc[key]) acc[key] = [];
    acc[key].push(u);
    return acc;
  }, {});

  return (
    <div className="realease-card overflow-hidden" data-testid="client-workspace-section">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003E40] to-[#008A8D] text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          <div className="min-w-0">
            <h2 className="text-lg font-bold">Espace de travail client</h2>
            <p className="text-xs text-white/70 truncate">Vue agregée du dossier {data.lead.first_name} {data.lead.last_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowAddProject(true)} className="px-3 py-1.5 text-xs bg-white/15 hover:bg-white/25 rounded-lg font-semibold inline-flex items-center gap-1.5" data-testid="ws-add-project">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Projet
          </button>
          <button onClick={() => setShowAddDelay(true)} className="px-3 py-1.5 text-xs bg-white/15 hover:bg-white/25 rounded-lg font-semibold inline-flex items-center gap-1.5" data-testid="ws-add-delay">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Delai
          </button>
          <button onClick={() => setShowAddNote(true)} className="px-3 py-1.5 text-xs bg-white/15 hover:bg-white/25 rounded-lg font-semibold inline-flex items-center gap-1.5" data-testid="ws-add-note">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            Note
          </button>
          <button onClick={() => setCollapsed(c => !c)} className="text-white/70 hover:text-white ml-2" data-testid="workspace-toggle">
            <svg className={`w-5 h-5 transition-transform ${collapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* === MANDAT === */}
          <Panel title="Mandat de recherche" icon="docs" testid="ws-mandat">
            {signedContract ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Signe</span>
                  <span className="font-semibold text-gray-900">{signedContract.number}</span>
                </div>
                <p className="text-xs text-gray-600">Signe le {fmtDate(signedContract.signed_at)} par <b>{signedContract.signed_full_name}</b></p>
                {signedContract.signed_address && (
                  <p className="text-xs text-gray-500 mt-1">{signedContract.signed_address}, {signedContract.signed_postal_code} {signedContract.signed_city}</p>
                )}
                {signedContract.drive_file_url ? (
                  <a href={signedContract.drive_file_url} target="_blank" rel="noreferrer" className="text-xs text-[#008A8D] hover:underline inline-flex items-center gap-1 mt-2">
                    Voir l'archive Drive →
                  </a>
                ) : (
                  <p className="text-xs text-amber-700 mt-2">Non encore archive sur Drive. Allez sur la section Mandat pour le pousser.</p>
                )}
              </div>
            ) : draftContracts.length > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">En attente de signature</p>
                {draftContracts.slice(0, 2).map(c => (
                  <div key={c.contract_id} className="text-xs text-gray-700">{c.number} - {c.status}</div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">Aucun mandat cree.</p>
            )}
          </Panel>

          {/* === COURTIER === */}
          <Panel title="Courtier assigne" icon="user" testid="ws-courtier">
            {courtier ? (
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="font-semibold text-gray-900">{courtier.name}</div>
                <a href={`mailto:${courtier.email}`} className="text-xs text-[#008A8D] hover:underline">{courtier.email}</a>
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">Aucun courtier assigne pour l'instant.</p>
            )}
          </Panel>

          {/* === PIECES DEPOSEES (full width) === */}
          <div className="lg:col-span-2">
            <Panel
              title="Pieces justificatives deposees par le client"
              icon="cloud"
              testid="ws-pieces"
              actions={
                <div className="flex items-center gap-2">
                  {link?.token && (
                    <>
                      <button onClick={copyLink} className="text-xs px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium" data-testid="copy-upload-link">
                        {copied ? 'Copie !' : 'Copier le lien'}
                      </button>
                      <button onClick={revokeLink} className="text-xs px-2.5 py-1 rounded-lg text-red-600 hover:bg-red-50 font-medium" data-testid="revoke-upload-link">
                        Revoquer
                      </button>
                    </>
                  )}
                  {uploads.length > 0 && (
                    <button onClick={exportZip} disabled={exportingZip} className="text-xs px-3 py-1 rounded-lg bg-[#A57F4F] hover:bg-[#8a6a42] text-white font-semibold disabled:opacity-50" data-testid="export-docs-zip">
                      {exportingZip ? 'Préparation...' : 'Exporter le dossier (zip)'}
                    </button>
                  )}
                  {uploads.length > 0 && data?.courtier?.email && (
                    <button onClick={sendToCourtier} disabled={sendingCourtier} className="text-xs px-3 py-1 rounded-lg bg-[#13736D] hover:bg-[#0F5C57] text-white font-semibold disabled:opacity-50" data-testid="send-docs-courtier">
                      {sendingCourtier ? 'Envoi...' : 'Envoyer au courtier'}
                    </button>
                  )}
                  <button onClick={generateLink} disabled={sendingLink} className="text-xs px-3 py-1 rounded-lg bg-[#008A8D] hover:bg-[#003E40] text-white font-semibold disabled:opacity-50" data-testid="send-upload-link">
                    {sendingLink ? 'Envoi...' : (link?.token ? 'Redemander les documents (email)' : 'Demander les documents (email)')}
                  </button>
                </div>
              }
            >
              {/* === JAUGE DE COMPLETUDE DES PIECES OBLIGATOIRES === */}
              {docStatus && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3" data-testid="docs-completeness-gauge">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-bold text-gray-800">
                      Complétude du dossier : {docStatus.required_received}/{docStatus.required_total} pièces obligatoires
                    </span>
                    <div className="flex items-center gap-2">
                      {docStatus.last_reminder_at && (
                        <span className="text-[10px] text-gray-400">Dernière relance : {fmtDT(docStatus.last_reminder_at)}</span>
                      )}
                      {docStatus.missing_required.length > 0 && link?.token && (
                        <button onClick={sendReminder} disabled={sendingReminder}
                                className="text-xs px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold disabled:opacity-50"
                                data-testid="send-docs-reminder">
                          {sendingReminder ? 'Envoi...' : 'Relancer le client'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-2" data-testid="docs-progress-bar">
                    <div className={`h-full rounded-full transition-all ${docStatus.completeness_pct >= 100 ? 'bg-emerald-500' : docStatus.completeness_pct >= 50 ? 'bg-[#008A8D]' : 'bg-amber-500'}`}
                         style={{ width: `${docStatus.completeness_pct}%` }} />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {docStatus.items.map(it => (
                      <span key={it.key}
                            title={it.label}
                            data-testid={`doc-chip-${it.key}`}
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                              it.uploaded_count > 0
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : it.required
                                  ? 'bg-red-50 text-red-600 border-red-200'
                                  : 'bg-gray-50 text-gray-400 border-gray-200'
                            }`}>
                        {it.uploaded_count > 0 ? '✓' : (it.required ? '✗' : '·')} {it.label.split('—')[0].split('(')[0].trim()}
                        {it.uploaded_count > 0 && ` (${it.uploaded_count})`}
                      </span>
                    ))}
                  </div>
                  {docStatus.missing_required.length === 0 && (
                    <p className="text-[11px] text-emerald-600 font-semibold mt-2" data-testid="docs-complete-msg">✓ Toutes les pièces obligatoires sont reçues.</p>
                  )}
                </div>
              )}

              {link?.token && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 mb-3 text-xs flex items-center justify-between gap-3">
                  <div>
                    <span className="font-semibold text-teal-800">Lien actif</span>
                    <span className="text-gray-600 ml-2">- expire le {fmtDate(link.expires_at)} - {uploads.length} piece(s) recue(s)</span>
                  </div>
                  {link.last_upload_at && <span className="text-gray-500 text-[10px]">Dernier depot : {fmtDT(link.last_upload_at)}</span>}
                </div>
              )}

              {uploads.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Aucune piece deposee pour l'instant.</p>
              ) : (
                <div className="space-y-3">
                  {['Piece_identite', 'Bulletins_salaire', 'Avis_imposition', 'Releves_bancaires', 'Justificatifs_epargne', 'Autre'].map(type => {
                    const files = uploadsByType[type];
                    if (!files || files.length === 0) return null;
                    return (
                      <div key={type} className="border border-gray-200 rounded-lg p-3" data-testid={`ws-pieces-${type}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm text-gray-800">{DOC_TYPE_LABEL[type] || type}</span>
                          <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">{files.length} fichier(s)</span>
                        </div>
                        <div className="space-y-1">
                          {files.map(f => (
                            <a key={f.file_id} href={f.url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 text-xs text-gray-700 hover:bg-gray-50 rounded px-2 py-1 -mx-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                <span className="truncate">{f.filename}</span>
                              </div>
                              <span className="text-gray-400 text-[10px] shrink-0">{fmtDT(f.uploaded_at)}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {drive?.subfolders?.['Doc admins clients']?.url && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <a href={drive.subfolders['Doc admins clients'].url} target="_blank" rel="noreferrer" className="text-xs text-[#008A8D] hover:underline inline-flex items-center gap-1">
                    Ouvrir le dossier admin sur Drive →
                  </a>
                </div>
              )}
            </Panel>
          </div>

          {/* === PROJETS === */}
          <Panel title={`Projets en cours (${projects.length})`} icon="building" testid="ws-projects">
            {projects.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Aucun projet ouvert sur ce client.</p>
            ) : (
              <div className="space-y-2">
                {projects.slice(0, 5).map(p => (
                  <a key={p.project_id} href={`/projects/${p.project_id}`} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-2.5 hover:border-[#008A8D] transition-colors">
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{p.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">{p.status} - {p.geographic_zone || 'zone non definie'}</div>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </a>
                ))}
              </div>
            )}
          </Panel>

          {/* === DELAIS OUVERTS === */}
          <Panel title={`Delais ouverts (${delays.length})`} icon="clock" testid="ws-delays">
            {delays.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Aucun delai ouvert.</p>
            ) : (
              <div className="space-y-2">
                {delays.slice(0, 6).map(d => {
                  const overdue = d.due_date && new Date(d.due_date) < new Date();
                  return (
                    <div key={d.delay_id} className={`text-xs border rounded-lg p-2 ${overdue ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-900 truncate">{d.title || d.type}</span>
                        <span className={`text-[10px] uppercase font-bold ${overdue ? 'text-red-700' : 'text-gray-500'}`}>{fmtDate(d.due_date)}</span>
                      </div>
                      {d.description && <div className="text-gray-500 mt-0.5 truncate">{d.description}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>

          {/* === NOTES RECENTES (full width) === */}
          {data.recent_notes && data.recent_notes.length > 0 && (
            <div className="lg:col-span-2">
              <Panel title="Notes recentes" icon="note" testid="ws-notes">
                <div className="space-y-2">
                  {data.recent_notes.slice(0, 4).map((n, i) => (
                    <div key={i} className="bg-amber-50/60 border border-amber-200 rounded-lg p-3 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase font-bold tracking-wider text-amber-800">{n.source}</span>
                        <span className="text-gray-500">{fmtDT(n.updated_at)}</span>
                      </div>
                      <div className="text-gray-700 whitespace-pre-line">{(n.content || '').slice(0, 280)}{(n.content || '').length > 280 ? '…' : ''}</div>
                      {n.author && <div className="text-[10px] text-gray-500 mt-1">par {n.author}</div>}
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          )}

        </div>
      )}

      {/* === MODALS ACTIONS RAPIDES === */}
      {showAddProject && (
        <AddProjectModal
          defaultLeadId={leadId}
          defaultLeadName={`${data.lead.first_name || ''} ${data.lead.last_name || ''}`.trim()}
          onClose={() => setShowAddProject(false)}
          onSuccess={() => { setShowAddProject(false); toast.success('Projet cree.'); load(); }}
        />
      )}
      {showAddDelay && (
        <AddDelayModal
          projects={allProjects}
          onClose={() => setShowAddDelay(false)}
          onSuccess={() => { setShowAddDelay(false); toast.success('Delai ajoute.'); load(); }}
        />
      )}
      {showAddNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full" data-testid="ws-note-modal">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-[#003E40]">Ajouter une note rapide</h3>
              <button onClick={() => { setShowAddNote(false); setNoteText(''); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-5">
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={6}
                placeholder="• Echange tel du jour&#10;• Points a relancer&#10;• Decisions prises..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]"
                data-testid="ws-note-textarea"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">La note sera horodatee et ajoutee en haut des notes existantes de la fiche lead.</p>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-end gap-2 rounded-b-2xl">
              <button onClick={() => { setShowAddNote(false); setNoteText(''); }} className="text-sm px-3 py-1.5 text-gray-600 hover:text-gray-800">Annuler</button>
              <button
                onClick={saveQuickNote}
                disabled={savingNote || !noteText.trim()}
                className="text-sm px-4 py-1.5 bg-[#008A8D] hover:bg-[#003E40] text-white font-semibold rounded-lg disabled:opacity-50"
                data-testid="ws-save-note"
              >
                {savingNote ? 'Sauvegarde...' : 'Enregistrer la note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, icon, actions, testid, children }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
          <PanelIcon name={icon} />
          {title}
        </h3>
        {actions}
      </div>
      {children}
    </div>
  );
}

function PanelIcon({ name }) {
  const paths = {
    docs: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
    user: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />,
    cloud: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />,
    building: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />,
    clock: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    note: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
  };
  return <svg className="w-4 h-4 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">{paths[name] || paths.docs}</svg>;
}
